import { useState, useMemo, useCallback, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  Divider,
  Select,
  MenuItem,
  LinearProgress,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import {
  IWpkNode,
  IWpkAccountAttrs,
  IWpkCharacteristics,
  DEFAULT_CHARACTERISTICS,
} from "data/wpk_gminy";
import {
  getParser,
  loadSelectedFkSystem,
  applyCodeNorm,
  isValidCode,
  detectCol,
  detectEncoding,
  detectCsvSep,
} from "config/fkParsers";
import {
  useChartsOfAccounts,
  useCreateChartOfAccounts,
  useUpdateChartOfAccounts,
  useDeleteChartOfAccounts,
  useSetActiveChartOfAccounts,
  parseTreeJson,
} from "api/accountingService";
import styles from "../JrwaTab/styles.module.scss";
import cardStyles from "./cardStyles.module.scss";

// ── Typy ─────────────────────────────────────────────────────────────────────

interface IUserPlan {
  id: string;
  title: string;
  templateId: string;
  tree: IWpkNode[];
  isActivePostingPlan: boolean;
}

// ── Tree operations ───────────────────────────────────────────────────────────

const countNodes = (nodes: IWpkNode[]): number => {
  let n = 0;
  const visit = (node: IWpkNode) => { n++; node.children.forEach(visit); };
  nodes.forEach(visit);
  return n;
};

const collectAllIds = (nodes: IWpkNode[]): Set<string> => {
  const ids = new Set<string>();
  const visit = (n: IWpkNode) => {
    if (n.children.length > 0) ids.add(n.id);
    n.children.forEach(visit);
  };
  nodes.forEach(visit);
  return ids;
};

const filterTree = (nodes: IWpkNode[], q: string): [IWpkNode[], Set<string>] => {
  const expanded = new Set<string>();
  const lower = q.toLowerCase();
  const filterNode = (n: IWpkNode): IWpkNode | null => {
    const filteredChildren = n.children
      .map(filterNode)
      .filter(Boolean) as IWpkNode[];
    const selfMatch =
      n.label.toLowerCase().includes(lower) ||
      n.code.toLowerCase().includes(lower);
    if (selfMatch || filteredChildren.length > 0) {
      if (filteredChildren.length > 0) expanded.add(n.id);
      return { ...n, children: filteredChildren };
    }
    return null;
  };
  const result = nodes.map(filterNode).filter(Boolean) as IWpkNode[];
  return [result, expanded];
};

const insertNode = (tree: IWpkNode[], parentId: string, newNode: IWpkNode): IWpkNode[] =>
  tree.map((n) => {
    if (n.id === parentId) return { ...n, children: [...n.children, newNode] };
    return { ...n, children: insertNode(n.children, parentId, newNode) };
  });

const insertNodeAfter = (tree: IWpkNode[], afterId: string, newNode: IWpkNode): IWpkNode[] => {
  const idx = tree.findIndex((n) => n.id === afterId);
  if (idx !== -1) {
    const result = [...tree];
    result.splice(idx + 1, 0, newNode);
    return result;
  }
  return tree.map((n) => ({ ...n, children: insertNodeAfter(n.children, afterId, newNode) }));
};

const updateLabel = (tree: IWpkNode[], id: string, label: string): IWpkNode[] =>
  tree.map((n) => {
    if (n.id === id) return { ...n, label };
    return { ...n, children: updateLabel(n.children, id, label) };
  });

const updateCode = (tree: IWpkNode[], id: string, code: string): IWpkNode[] =>
  tree.map((n) => {
    if (n.id === id) return { ...n, code };
    return { ...n, children: updateCode(n.children, id, code) };
  });

const removeNode = (tree: IWpkNode[], id: string): IWpkNode[] =>
  tree
    .filter((n) => n.id !== id)
    .map((n) => ({ ...n, children: removeNode(n.children, id) }));

const updateCharacteristics = (tree: IWpkNode[], id: string, ch: IWpkCharacteristics): IWpkNode[] =>
  tree.map((n) => {
    if (n.id === id) return { ...n, characteristics: ch };
    return { ...n, children: updateCharacteristics(n.children, id, ch) };
  });

const findNode = (tree: IWpkNode[], id: string): IWpkNode | null => {
  for (const n of tree) {
    if (n.id === id) return n;
    const found = findNode(n.children, id);
    if (found) return found;
  }
  return null;
};

// ── Import XLS — typy i pomocnicze ───────────────────────────────────────────

type CellValue = string | number | boolean | null | undefined;

export const ZOIS_BO_KEY = "zois_opening_balance_v1";

interface IImportState {
  fileName: string;
  headers: string[];
  rows: CellValue[][];
  codeCol: number;
  nameCol: number;
  boWnCol: number;
  boMaCol: number;
  planTitle: string;
  setAsActive: boolean;
  importBo: boolean;
  boDate: string;
}

const cellStr = (v: CellValue): string => (v == null ? "" : String(v).trim());

const parsePolishNumber = (v: CellValue): number => {
  if (typeof v === "number") return v;
  const s = cellStr(v).replace(/\s/g, "").replace(/\./g, "").replace(",", ".");
  return parseFloat(s) || 0;
};

// Normalizacja kodu konta wg reguł aktualnie wybranego systemu FK.
// Pobiera parser z rejestru (config/fkParsers), dzięki czemu zasady
// zmieniają się automatycznie przy zmianie systemu w zakładce Migracja danych.
const normalizeAccountCode = (raw: string): string =>
  applyCodeNorm(raw, getParser(loadSelectedFkSystem()));

const findImportParentCode = (code: string, byCode: Map<string, IWpkNode>): IWpkNode | null => {
  const parts = code.split("-");
  for (let i = parts.length - 1; i > 0; i--) {
    const candidate = parts.slice(0, i).join("-");
    if (byCode.has(candidate)) return byCode.get(candidate)!;
  }
  if (!code.includes("-")) {
    for (let len = code.length - 1; len > 0; len--) {
      const candidate = code.substring(0, len);
      if (byCode.has(candidate)) return byCode.get(candidate)!;
    }
  }
  return null;
};

// Powszechnie przyjęta terminologia Zespołów wg polskiego prawa bilansowego
const ZESPOL_LABELS: Record<string, string> = {
  "0": "Zespół 0 – Majątek trwały",
  "1": "Zespół 1 – Środki pieniężne i rachunki bankowe",
  "2": "Zespół 2 – Rozrachunki i roszczenia",
  "3": "Zespół 3 – Materiały i towary",
  "4": "Zespół 4 – Koszty według rodzajów i ich rozliczenie",
  "5": "Zespół 5 – Koszty według typów działalności i ich rozliczenie",
  "6": "Zespół 6 – Produkty i rozliczenia międzyokresowe",
  "7": "Zespół 7 – Przychody, dochody i koszty",
  "8": "Zespół 8 – Fundusze własne i wynik finansowy",
  "9": "Zespół 9 – Rozliczenia międzyokresowe kosztów i przychodów",
};

const buildTreeFromImport = (accounts: { code: string; name: string }[]): IWpkNode[] => {
  // Mapa kod → nazwa (tylko konta jawnie podane w pliku)
  const explicit = new Map<string, string>();
  for (const { code, name } of accounts) {
    if (code && !explicit.has(code)) explicit.set(code, name);
  }

  // Dobuduj brakujące węzły pośrednie dla kodów z separatorem "-"
  // np. "011-2-01" wymaga "011-2" i "011" (jeśli nie ma ich w pliku).
  // Zawsze dodaj też jednocyfrowy węzeł Zespołu dla każdego kodu.
  const allCodes = new Set<string>(explicit.keys());
  for (const code of explicit.keys()) {
    if (code.includes("-")) {
      const parts = code.split("-");
      for (let i = 1; i < parts.length; i++) {
        allCodes.add(parts.slice(0, i).join("-"));
      }
    }
    // Jednocyfrowy przodek = Zespół (pierwsza cyfra kodu konta)
    const firstDigit = code.match(/^(\d)/)?.[1];
    if (firstDigit) allCodes.add(firstDigit);
  }

  // Sortuj i utwórz węzły
  const sorted = [...allCodes].sort((a, b) =>
    a.localeCompare(b, undefined, { numeric: true }),
  );
  let seq = 0;
  const byCode = new Map<string, IWpkNode>();
  for (const code of sorted) {
    const isZespol = /^\d$/.test(code);
    byCode.set(code, {
      id: `import-${code.replace(/[^a-z0-9]/gi, "-")}-${seq++}`,
      code,
      // Zespoły zawsze dostają standardową etykietę wg terminologii bilansowej
      label: isZespol
        ? (ZESPOL_LABELS[code] ?? `Zespół ${code}`)
        : (explicit.get(code) ?? code),
      attrs: { syntetyczne: true, analityczne: false, zbiorcze: false, bilansowe: true },
      children: [],
      custom: true,
    });
  }

  const roots: IWpkNode[] = [];
  for (const node of byCode.values()) {
    const parent = findImportParentCode(node.code, byCode);
    if (parent) parent.children.push(node);
    else roots.push(node);
  }
  return roots;
};

// ── Karta charakterystyki — panel slide ───────────────────────────────────────

interface CardPanelProps {
  node: IWpkNode;
  onClose: () => void;
  onSave: (ch: IWpkCharacteristics, label: string) => void;
}

const CardPanel = ({ node, onClose, onSave }: CardPanelProps) => {
  const [ch, setCh] = useState<IWpkCharacteristics>(
    node.characteristics ?? { ...DEFAULT_CHARACTERISTICS },
  );
  const [label, setLabel] = useState(node.label);

  const set = <K extends keyof IWpkCharacteristics>(key: K, val: IWpkCharacteristics[K]) =>
    setCh((prev) => ({ ...prev, [key]: val }));

  return createPortal(
    <div className={cardStyles.overlay} onClick={onClose}>
      <div className={cardStyles.panel} onClick={(e) => e.stopPropagation()}>
        <div className={cardStyles.header}>
          <div className={cardStyles.headerTitle}>
            <ArticleOutlinedIcon sx={{ fontSize: 18, color: "#5d49c7" }} />
            <span>Karta konta</span>
          </div>
          <button className={cardStyles.closeBtn} onClick={onClose}>
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>

        <div className={cardStyles.accountId}>
          <span className={cardStyles.accountCode}>{node.code}</span>
          <TextField
            size="small"
            fullWidth
            label="Nazwa konta"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            sx={{ mt: 0.5 }}
          />
        </div>

        <Divider sx={{ mx: 2 }} />

        <div className={cardStyles.body}>
          <div className={cardStyles.section}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={ch.bilansowe}
                  onChange={(e) => {
                    set("bilansowe", e.target.checked);
                    if (!e.target.checked) set("typBilansowy", null);
                  }}
                />
              }
              label={<span className={cardStyles.sectionTitle}>Konto bilansowe</span>}
            />
            {ch.bilansowe && (
              <FormControl component="fieldset" sx={{ pl: 3.5 }}>
                <FormLabel component="legend" sx={{ fontSize: 12, color: "#6e6779", mb: 0.5 }}>
                  Charakter konta
                </FormLabel>
                <RadioGroup
                  value={ch.typBilansowy ?? ""}
                  onChange={(e) => set("typBilansowy", e.target.value as IWpkCharacteristics["typBilansowy"])}
                >
                  <FormControlLabel value="aktywa" control={<Radio size="small" />} label={<span className={cardStyles.radioLabel}>Aktywa</span>} />
                  <FormControlLabel value="pasywa" control={<Radio size="small" />} label={<span className={cardStyles.radioLabel}>Pasywa</span>} />
                  <FormControlLabel value="aktywoPasywne" control={<Radio size="small" />} label={<span className={cardStyles.radioLabel}>Aktywo-pasywne (dwustronne)</span>} />
                </RadioGroup>
              </FormControl>
            )}
          </div>

          <div className={cardStyles.section}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={ch.wynikowe}
                  onChange={(e) => {
                    set("wynikowe", e.target.checked);
                    if (!e.target.checked) set("typWynikowy", null);
                  }}
                />
              }
              label={<span className={cardStyles.sectionTitle}>Konto wynikowe</span>}
            />
            {ch.wynikowe && (
              <FormControl component="fieldset" sx={{ pl: 3.5 }}>
                <FormLabel component="legend" sx={{ fontSize: 12, color: "#6e6779", mb: 0.5 }}>
                  Charakter konta
                </FormLabel>
                <RadioGroup
                  value={ch.typWynikowy ?? ""}
                  onChange={(e) => set("typWynikowy", e.target.value as IWpkCharacteristics["typWynikowy"])}
                >
                  <FormControlLabel value="przychody" control={<Radio size="small" />} label={<span className={cardStyles.radioLabel}>Przychody</span>} />
                  <FormControlLabel value="koszty" control={<Radio size="small" />} label={<span className={cardStyles.radioLabel}>Koszty</span>} />
                </RadioGroup>
              </FormControl>
            )}
          </div>

          <div className={cardStyles.section}>
            <span className={cardStyles.sectionTitle}>Uwagi</span>
            <TextField
              multiline
              minRows={3}
              size="small"
              fullWidth
              placeholder="Dodatkowy opis lub uwagi do konta…"
              value={ch.uwagi}
              onChange={(e) => set("uwagi", e.target.value)}
              sx={{ mt: 0.5 }}
            />
          </div>
        </div>

        <div className={cardStyles.footer}>
          <Button size="small" color="inherit" onClick={onClose}>Anuluj</Button>
          <Button size="small" variant="contained" disabled={!label.trim()} onClick={() => { onSave(ch, label.trim()); onClose(); }}>Zapisz</Button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

// ── TreeNode ──────────────────────────────────────────────────────────────────

interface TreeNodeProps {
  node: IWpkNode;
  depth: number;
  expanded: Set<string>;
  onToggle: (id: string) => void;
  onAdd: (parentId: string, parentLabel: string, parentCode: string) => void;
  onAddSibling: (nodeId: string) => void;
  onDelete: (id: string, label: string) => void;
  onOpenCard: (id: string) => void;
  searchActive: boolean;
  inlineEditId: string | null;
  inlineLabel: string;
  inlineCode: string;
  onInlineChange: (val: string) => void;
  onInlineCodeChange: (val: string) => void;
  onInlineSave: () => void;
  onInlineBlur: () => void;
  onInlineFocus: () => void;
  onInlineCancel: () => void;
  onStartEdit: (id: string, label: string, code: string) => void;
}

const TreeNode = ({
  node, depth, expanded, onToggle, onAdd, onAddSibling, onDelete, onOpenCard, searchActive,
  inlineEditId, inlineLabel, inlineCode, onInlineChange, onInlineCodeChange,
  onInlineSave, onInlineBlur, onInlineFocus, onInlineCancel, onStartEdit,
}: TreeNodeProps) => {
  const hasChildren = node.children.length > 0;
  const isOpen = expanded.has(node.id);
  const isInlineEdit = node.id === inlineEditId;
  const isZespol = /^\d$/.test(node.code);
  const hasCard = !!node.characteristics &&
    (node.characteristics.bilansowe || node.characteristics.wynikowe || !!node.characteristics.uwagi);

  return (
    <div className={styles.nodeWrapper}>
      <div
        className={`${styles.nodeRow} ${depth === 0 ? styles.nodeRowL1 : ""} ${depth === 1 ? styles.nodeRowL2 : ""}`}
        style={{ paddingLeft: 8 + depth * 20 }}
      >
        {isInlineEdit ? (
          <>
            <span className={styles.chevron} style={{ visibility: "hidden" }}>
              <ChevronRightIcon sx={{ fontSize: 16 }} />
            </span>
            <input
              className={styles.inlineCodeInput}
              value={inlineCode}
              placeholder="nr"
              onChange={(e) => onInlineCodeChange(e.target.value)}
              onFocus={onInlineFocus}
              onBlur={onInlineBlur}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); onInlineSave(); }
                if (e.key === "Escape") { e.preventDefault(); onInlineCancel(); }
                if (e.key === "Tab") { e.preventDefault(); (e.currentTarget.nextElementSibling as HTMLInputElement | null)?.focus(); }
              }}
            />
            <input
              className={styles.inlineInput}
              autoFocus
              value={inlineLabel}
              placeholder="Nazwa konta…"
              onChange={(e) => onInlineChange(e.target.value)}
              onFocus={onInlineFocus}
              onBlur={onInlineBlur}
              onKeyDown={(e) => {
                if (e.key === "Enter") { e.preventDefault(); onInlineSave(); }
                if (e.key === "Escape") { e.preventDefault(); onInlineCancel(); }
              }}
            />
            <span style={{ display: "flex", gap: 2, flexShrink: 0 }}>
              <Tooltip title="Zatwierdź" placement="top" arrow>
                <span
                  className={styles.actionBtn}
                  style={{ opacity: 1, fontSize: 13, fontWeight: 700, color: "#2a8429" }}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={onInlineSave}
                >✓</span>
              </Tooltip>
              <Tooltip title="Usuń" placement="top" arrow>
                <span
                  className={`${styles.actionBtn} ${styles.actionBtnDanger}`}
                  style={{ opacity: 1 }}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={onInlineCancel}
                >✕</span>
              </Tooltip>
            </span>
          </>
        ) : (
          <>
            <span
              className={styles.chevron}
              onClick={() => hasChildren && onToggle(node.id)}
              style={{ visibility: hasChildren ? "visible" : "hidden" }}
            >
              {isOpen || searchActive ? <ExpandMoreIcon sx={{ fontSize: 16 }} /> : <ChevronRightIcon sx={{ fontSize: 16 }} />}
            </span>

            {!isZespol && (
              <Tooltip title="Dodaj konto podrzędne" placement="top" arrow>
                <span className={styles.nodeAddLeft} onClick={(e) => { e.stopPropagation(); onAdd(node.id, node.label, node.code); }}>
                  <AddIcon sx={{ fontSize: 13 }} />
                </span>
              </Tooltip>
            )}

            <span className={`${styles.codeChip} ${node.custom ? styles.codeChipCustom : ""}`}>{node.code}</span>
            <span
              className={`${styles.nodeLabel} ${node.custom ? styles.nodeLabelCustom : ""}`}
              onDoubleClick={(e) => { e.stopPropagation(); onStartEdit(node.id, node.label, node.code); }}
            >{node.label}</span>

            <span className={styles.nodeActions}>
              {!isZespol && (
                <Tooltip title="Dodaj konto na tym samym poziomie" placement="top" arrow>
                  <span className={styles.actionBtn} onClick={(e) => { e.stopPropagation(); onAddSibling(node.id); }}>
                    <AddIcon sx={{ fontSize: 13 }} />
                  </span>
                </Tooltip>
              )}
              <Tooltip title="Karta charakterystyki konta" placement="top" arrow>
                <span
                  className={`${styles.actionBtn} ${hasCard ? styles.actionBtnActive : ""}`}
                  onClick={(e) => { e.stopPropagation(); onOpenCard(node.id); }}
                >
                  <ArticleOutlinedIcon sx={{ fontSize: 13 }} />
                </span>
              </Tooltip>
              {!isZespol && (
                <Tooltip title="Edytuj konto" placement="top" arrow>
                  <span className={styles.actionBtn} onClick={(e) => { e.stopPropagation(); onOpenCard(node.id); }}>
                    <EditOutlinedIcon sx={{ fontSize: 13 }} />
                  </span>
                </Tooltip>
              )}
              {node.custom && !isZespol && (
                <Tooltip title="Usuń" placement="top" arrow>
                  <span className={`${styles.actionBtn} ${styles.actionBtnDanger}`} onClick={(e) => { e.stopPropagation(); onDelete(node.id, node.label); }}>
                    <DeleteOutlineIcon sx={{ fontSize: 13 }} />
                  </span>
                </Tooltip>
              )}
            </span>
          </>
        )}
      </div>

      {hasChildren && (isOpen || searchActive) && (
        <div>
          {node.children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              onToggle={onToggle}
              onAdd={onAdd}
              onAddSibling={onAddSibling}
              onDelete={onDelete}
              onOpenCard={onOpenCard}
              searchActive={searchActive}
              inlineEditId={inlineEditId}
              inlineLabel={inlineLabel}
              inlineCode={inlineCode}
              onInlineChange={onInlineChange}
              onInlineCodeChange={onInlineCodeChange}
              onInlineSave={onInlineSave}
              onInlineBlur={onInlineBlur}
              onInlineFocus={onInlineFocus}
              onInlineCancel={onInlineCancel}
              onStartEdit={onStartEdit}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ── PlanTree — edytor drzewa kont ─────────────────────────────────────────────

const DEFAULT_ATTRS: IWpkAccountAttrs = {
  syntetyczne: true,
  analityczne: false,
  zbiorcze: false,
  bilansowe: true,
};

interface PlanTreeProps {
  tree: IWpkNode[];
  onTreeChange: (tree: IWpkNode[]) => void;
}

const PlanTree = ({ tree, onTreeChange }: PlanTreeProps) => {
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState("");

  const [inlineEditId, setInlineEditId] = useState<string | null>(null);
  const [inlineLabel, setInlineLabel] = useState("");
  const [inlineCode, setInlineCode] = useState("");
  const [inlineIsNew, setInlineIsNew] = useState(false);
  const [inlineOrigLabel, setInlineOrigLabel] = useState("");
  const [inlineOrigCode, setInlineOrigCode] = useState("");
  const inlineBlurTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{ id: string; label: string } | null>(null);
  const [cardNodeId, setCardNodeId] = useState<string | null>(null);

  const [filteredTree, searchExpanded] = useMemo(() => {
    if (!search.trim()) return [tree, new Set<string>()] as const;
    return filterTree(tree, search.trim());
  }, [tree, search]);

  const searchActive = search.trim().length > 0;

  const handleToggle = useCallback((id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleAddSibling = (nodeId: string) => {
    const triggerNode = findNode(tree, nodeId);
    const triggerCode = triggerNode?.code ?? "";
    const parts = triggerCode.split("-");
    const lastPart = parts[parts.length - 1];
    const num = parseInt(lastPart, 10);
    const nextPart = !isNaN(num)
      ? String(num + 1).padStart(lastPart.length, "0")
      : "01";
    const code = parts.length > 1
      ? `${parts.slice(0, -1).join("-")}-${nextPart}`
      : nextPart;

    const id = `custom-${Date.now()}`;
    const newNode: IWpkNode = { id, code, label: "", attrs: { ...DEFAULT_ATTRS }, children: [], custom: true };

    onTreeChange(insertNodeAfter(tree, nodeId, newNode));
    setInlineEditId(id);
    setInlineLabel("");
    setInlineCode(code);
    setInlineIsNew(true);
    setInlineOrigLabel("");
    setInlineOrigCode(code);
  };

  const handleAddChild = (parentId: string, _parentLabel: string, parentCode: string) => {
    const parent = findNode(tree, parentId);
    const siblings = parent?.children ?? [];
    let maxOrdinal = 0;
    for (const sibling of siblings) {
      const lastPart = sibling.code.split("-").pop() ?? "";
      const num = parseInt(lastPart, 10);
      if (!isNaN(num)) maxOrdinal = Math.max(maxOrdinal, num);
    }
    const ordinal = String(maxOrdinal + 1).padStart(2, "0");
    const code = parentCode && parentCode !== "—" ? `${parentCode}-${ordinal}` : ordinal;
    const id = `custom-${Date.now()}`;
    const newNode: IWpkNode = { id, code, label: "", attrs: { ...DEFAULT_ATTRS }, children: [], custom: true };
    onTreeChange(insertNode(tree, parentId, newNode));
    setExpanded((prev) => new Set([...prev, parentId]));
    setInlineEditId(id);
    setInlineLabel("");
    setInlineCode(code);
    setInlineIsNew(true);
    setInlineOrigLabel("");
    setInlineOrigCode(code);
  };

  const startInlineEdit = (id: string, currentLabel: string, currentCode: string) => {
    setInlineEditId(id);
    setInlineLabel(currentLabel);
    setInlineCode(currentCode);
    setInlineIsNew(false);
    setInlineOrigLabel(currentLabel);
    setInlineOrigCode(currentCode);
  };

  const saveInline = () => {
    if (!inlineEditId) return;
    const label = inlineLabel.trim();
    const code = inlineCode.trim() || "—";
    let updated = tree;
    if (label !== inlineOrigLabel || (!inlineIsNew && label)) updated = updateLabel(updated, inlineEditId, label);
    else if (label) updated = updateLabel(updated, inlineEditId, label);
    if (code !== inlineOrigCode) updated = updateCode(updated, inlineEditId, code);
    onTreeChange(updated);
    setInlineEditId(null);
    setInlineLabel("");
    setInlineCode("");
    setInlineIsNew(false);
    setInlineOrigLabel("");
    setInlineOrigCode("");
  };

  const cancelInline = () => {
    if (!inlineEditId) return;
    if (inlineIsNew) {
      onTreeChange(removeNode(tree, inlineEditId));
    } else {
      let updated = tree;
      if (inlineOrigLabel !== inlineLabel) updated = updateLabel(updated, inlineEditId, inlineOrigLabel);
      if (inlineOrigCode !== inlineCode) updated = updateCode(updated, inlineEditId, inlineOrigCode);
      onTreeChange(updated);
    }
    setInlineEditId(null);
    setInlineLabel("");
    setInlineCode("");
    setInlineIsNew(false);
    setInlineOrigLabel("");
    setInlineOrigCode("");
  };

  const handleInlineBlur = () => {
    inlineBlurTimer.current = setTimeout(saveInline, 150);
  };

  const handleInlineFocus = () => {
    if (inlineBlurTimer.current !== null) {
      clearTimeout(inlineBlurTimer.current);
      inlineBlurTimer.current = null;
    }
  };

  const confirmDelete = () => {
    if (!deleteConfirm) return;
    onTreeChange(removeNode(tree, deleteConfirm.id));
    setDeleteConfirm(null);
  };

  const handleSaveCard = (id: string, ch: IWpkCharacteristics, label: string) => {
    let updated = updateCharacteristics(tree, id, ch);
    updated = updateLabel(updated, id, label);
    onTreeChange(updated);
  };

  const cardNode = cardNodeId ? findNode(tree, cardNodeId) : null;
  const displayTree = searchActive ? filteredTree : tree;
  const displayExpanded = searchActive ? searchExpanded : expanded;

  return (
    <div className={styles.planTreeWrapper}>
      <div className={styles.toolbar}>
        <TextField
          size="small"
          placeholder="Szukaj po numerze lub nazwie konta…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={styles.searchBox}
          InputProps={{
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch("")}>✕</IconButton>
              </InputAdornment>
            ) : (
              <InputAdornment position="end">
                <SearchIcon fontSize="small" sx={{ color: "rgba(0,0,0,0.3)" }} />
              </InputAdornment>
            ),
          }}
        />
        <div className={styles.toolbarRight}>
          <Tooltip title="Rozwiń wszystko">
            <span>
              <IconButton size="small" onClick={() => setExpanded(collectAllIds(tree))} disabled={searchActive}>
                <UnfoldMoreIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Zwiń wszystko">
            <span>
              <IconButton size="small" onClick={() => setExpanded(new Set())} disabled={searchActive}>
                <UnfoldLessIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </div>
      </div>

      <div className={styles.legend}>
        <span className={`${styles.legendDot} ${styles.legendDotSystem}`} />
        wzorzec (WPK)
        <span className={`${styles.legendDot} ${styles.legendDotCustom}`} style={{ marginLeft: 16 }} />
        własne konta
      </div>

      <div className={styles.treeContainer}>
        {displayTree.length === 0 ? (
          <div className={styles.emptyMsg}>Brak wyników dla „{search}"</div>
        ) : (
          displayTree.map((node) => (
            <TreeNode
              key={node.id}
              node={node}
              depth={0}
              expanded={displayExpanded}
              onToggle={handleToggle}
              onAdd={handleAddChild}
              onAddSibling={handleAddSibling}
              onDelete={(id, label) => setDeleteConfirm({ id, label })}
              onOpenCard={setCardNodeId}
              searchActive={searchActive}
              inlineEditId={inlineEditId}
              inlineLabel={inlineLabel}
              inlineCode={inlineCode}
              onInlineChange={setInlineLabel}
              onInlineCodeChange={setInlineCode}
              onInlineSave={saveInline}
              onInlineBlur={handleInlineBlur}
              onInlineFocus={handleInlineFocus}
              onInlineCancel={cancelInline}
              onStartEdit={startInlineEdit}
            />
          ))
        )}
      </div>

      {cardNode && (
        <CardPanel
          node={cardNode}
          onClose={() => setCardNodeId(null)}
          onSave={(ch, label) => handleSaveCard(cardNode.id, ch, label)}
        />
      )}



      {/* Dialog: usuń */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 15, fontWeight: 600, pb: 1 }}>Usuń konto</DialogTitle>
        <DialogContent>
          Czy na pewno usunąć <strong>„{deleteConfirm?.label}"</strong>?
          <br />
          <span style={{ fontSize: 12, color: "rgba(0,0,0,0.5)", marginTop: 4, display: "block" }}>
            Zostaną usunięte również wszystkie konta podrzędne.
          </span>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirm(null)} color="inherit" size="small">Anuluj</Button>
          <Button onClick={confirmDelete} variant="contained" color="error" size="small">Usuń</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

// ── Main ──────────────────────────────────────────────────────────────────────

const WpkGminyTab = () => {
  const { data: rawPlans } = useChartsOfAccounts();
  const createMutation = useCreateChartOfAccounts();
  const updateMutation = useUpdateChartOfAccounts();
  const deleteMutation = useDeleteChartOfAccounts();
  const setActiveMutation = useSetActiveChartOfAccounts();

  // Lokalna kopia danych API — inicjalizowana raz po załadowaniu, potem zarządzana lokalnie
  const [userPlans, setUserPlans] = useState<IUserPlan[]>([]);
  const [plansInitialized, setPlansInitialized] = useState(false);
  const [activePlanId, setActivePlanId] = useState<string | null>(null);

  useEffect(() => {
    if (rawPlans !== undefined && !plansInitialized) {
      setUserPlans(rawPlans.map((p) => ({
        id: p.id,
        title: p.title,
        templateId: p.templateId,
        tree: parseTreeJson(p.treeJson),
        isActivePostingPlan: p.isActivePostingPlan,
      })));
      setPlansInitialized(true);
    }
  }, [rawPlans, plansInitialized]);

  const postingPlanId = userPlans.find((p) => p.isActivePostingPlan)?.id ?? null;

  const [deletePlanConfirm, setDeletePlanConfirm] = useState<{ id: string; title: string } | null>(null);

  const [sec2Collapsed, setSec2Collapsed] = useState(false);

  const [zoisPlanDialog, setZoisPlanDialog] = useState(false);
  const [zoisPlanTitle, setZoisPlanTitle] = useState("");

  const [importData, setImportData] = useState<IImportState | null>(null);
  const [importProgress, setImportProgress] = useState<{ value: number; label: string } | null>(null);
  const importFileRef = useRef<HTMLInputElement>(null);

  const handleImportFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = "";
    // WAŻNE: flushSync nie działa po await w React 18 — używamy zwykłego setState.
    // Pasek postępu może nie odświeżać się krok po kroku, ale import działa poprawnie.
    setImportProgress({ value: 5, label: "Ładowanie biblioteki…" });
    try {
      const XLSX = await import("xlsx");
      const isCsv = /\.(csv|txt)$/i.test(file.name);

      setImportProgress({ value: 20, label: "Odczyt pliku…" });
      const buffer = await file.arrayBuffer();
      let raw: CellValue[][];

      const parser = getParser(loadSelectedFkSystem());

      if (isCsv) {
        setImportProgress({ value: 40, label: "Wykrywanie kodowania…" });
        const bytes = new Uint8Array(buffer);
        const encoding = detectEncoding(bytes, parser);
        const text = new TextDecoder(encoding).decode(buffer);
        setImportProgress({ value: 60, label: "Parsowanie arkusza…" });
        const firstLine = text.split(/\r?\n/)[0] ?? "";
        const sep = detectCsvSep(firstLine, parser);
        const wb = XLSX.read(text, { type: "string", FS: sep });
        raw = XLSX.utils.sheet_to_json<CellValue[]>(wb.Sheets[wb.SheetNames[0]], { header: 1, raw: false, defval: "" });
      } else {
        setImportProgress({ value: 60, label: "Parsowanie arkusza…" });
        const wb = XLSX.read(buffer, { type: "array" });
        raw = XLSX.utils.sheet_to_json<CellValue[]>(wb.Sheets[wb.SheetNames[0]], { header: 1, raw: false, defval: "" });
      }

      setImportProgress({ value: 85, label: "Przetwarzanie wierszy…" });

      if (!raw || raw.length < 2) {
        setImportProgress(null);
        return;
      }
      const headers = (raw[0] as CellValue[]).map((h) => cellStr(h));
      const rows = raw.slice(1) as CellValue[][];

      // Wykrycie kolumn wg zasad wybranego systemu FK
      const hints = parser.columnHints;
      let codeCol = detectCol(headers, hints.code);
      let nameCol = detectCol(headers, hints.name);
      if (codeCol === -1) codeCol = 0;
      if (nameCol === -1 || nameCol === codeCol) nameCol = codeCol === 0 ? 1 : 0;
      let boWnCol = detectCol(headers, hints.boWn);
      let boMaCol = detectCol(headers, hints.boMa);
      if (boWnCol === -1) boWnCol = headers.findIndex((h, i) => i !== codeCol && i !== nameCol && h.toLowerCase().includes("wn"));
      if (boMaCol === -1) boMaCol = headers.findIndex((h, i) => i !== codeCol && i !== nameCol && h.toLowerCase().includes("ma"));
      if (boWnCol === -1) boWnCol = 2;
      if (boMaCol === -1) boMaCol = 3;

      setImportProgress(null);
      setImportData({
        fileName: file.name,
        headers,
        rows,
        codeCol,
        nameCol,
        boWnCol,
        boMaCol,
        planTitle: file.name.replace(/\.[^.]+$/, ""),
        setAsActive: false,
        importBo: false,
        boDate: `${new Date().getFullYear()}-01-01`,
      });
    } catch (err) {
      console.error("[WpkGminyTab] błąd importu pliku:", err);
      setImportProgress(null);
    }
  };

  const handleImportConfirm = () => {
    if (!importData || !importData.planTitle.trim()) return;

    const parser = getParser(loadSelectedFkSystem());
    const accounts = importData.rows
      .map((row) => ({ code: applyCodeNorm(cellStr(row[importData.codeCol]), parser), name: cellStr(row[importData.nameCol]) }))
      .filter((a) => isValidCode(a.code, parser));

    const importedTree = buildTreeFromImport(accounts);
    const title = importData.planTitle.trim();
    const isActivePostingPlan = importData.setAsActive;
    const capturedImportData = importData;

    // Zapisz plan w DB
    createMutation.mutate(
      { title, templateId: "wpk_gminy", treeJson: JSON.stringify(importedTree), isActivePostingPlan },
      {
        onSuccess: (created) => {
          const newPlan: IUserPlan = {
            id: created.id,
            title: created.title,
            templateId: created.templateId,
            tree: importedTree,
            isActivePostingPlan: created.isActivePostingPlan,
          };
          setUserPlans((prev) => {
            // Jeśli nowy plan ma być aktywny — odznacz pozostałe
            const base = isActivePostingPlan
              ? prev.map((p) => ({ ...p, isActivePostingPlan: false }))
              : prev;
            return [...base, newPlan];
          });
          setActivePlanId(created.id);

          if (capturedImportData.importBo) {
            const boEntries = capturedImportData.rows
              .map((row) => ({
                code: applyCodeNorm(cellStr(row[capturedImportData.codeCol]), parser),
                name: cellStr(row[capturedImportData.nameCol]),
                boWn: parsePolishNumber(row[capturedImportData.boWnCol]),
                boMa: parsePolishNumber(row[capturedImportData.boMaCol]),
              }))
              .filter((e) => isValidCode(e.code, parser));
            try {
              localStorage.setItem(ZOIS_BO_KEY, JSON.stringify({
                planId: created.id,
                planTitle: created.title,
                importedAt: new Date().toISOString(),
                boDate: capturedImportData.boDate,
                entries: boEntries,
              }));
              window.dispatchEvent(new Event("zois_bo_updated"));
            } catch { /* ignore */ }
          }
        },
      },
    );

    setImportData(null);
    setImportProgress(null);
  };

  const handleTogglePlan = (id: string) => {
    setActivePlanId((prev) => (prev === id ? null : id));
  };

  const handleSetPostingPlan = (id: string) => {
    const isCurrentlyActive = userPlans.find((p) => p.id === id)?.isActivePostingPlan ?? false;
    const nextActive = !isCurrentlyActive;
    // Optimistyczna aktualizacja stanu lokalnego
    setUserPlans((prev) => prev.map((p) => ({
      ...p,
      isActivePostingPlan: nextActive ? p.id === id : (p.id === id ? false : p.isActivePostingPlan),
    })));
    setActiveMutation.mutate({ id, isActive: nextActive });
  };

  const handleCreateFromZois = () => {
    if (!zoisPlanTitle.trim()) return;
    try {
      const raw = localStorage.getItem(ZOIS_BO_KEY);
      if (!raw) return;
      const data = JSON.parse(raw) as { entries?: { code: string; name?: string }[] };
      const accounts = (data.entries ?? [])
        .filter((e) => e.code && /\d/.test(e.code))
        .map((e) => ({ code: normalizeAccountCode(e.code), name: e.name ?? e.code }));
      const tree = buildTreeFromImport(accounts);
      const title = zoisPlanTitle.trim();

      createMutation.mutate(
        { title, templateId: "zois", treeJson: JSON.stringify(tree), isActivePostingPlan: false },
        {
          onSuccess: (created) => {
            setUserPlans((prev) => [
              ...prev,
              {
                id: created.id,
                title: created.title,
                templateId: created.templateId,
                tree,
                isActivePostingPlan: false,
              },
            ]);
            setActivePlanId(created.id);
          },
        },
      );
    } catch { /* ignore */ }
    setZoisPlanDialog(false);
    setZoisPlanTitle("");
  };

  const handleUserPlanTreeChange = (planId: string, tree: IWpkNode[]) => {
    const plan = userPlans.find((p) => p.id === planId);
    if (!plan) return;
    setUserPlans((prev) => prev.map((p) => (p.id === planId ? { ...p, tree } : p)));
    updateMutation.mutate({ id: planId, title: plan.title, treeJson: JSON.stringify(tree) });
  };

  const confirmDeletePlan = () => {
    if (!deletePlanConfirm) return;
    setUserPlans((prev) => prev.filter((p) => p.id !== deletePlanConfirm.id));
    if (activePlanId === deletePlanConfirm.id) setActivePlanId(null);
    deleteMutation.mutate(deletePlanConfirm.id);
    setDeletePlanConfirm(null);
  };

  return (
    <div className={styles.plansWrapper}>

      {/* Input poza sectionHeader — unika propagacji zdarzeń kliknięcia do nagłówka */}
      <input
        ref={importFileRef}
        type="file"
        accept=".xls,.xlsx,.ods,.csv,.txt"
        style={{ display: "none" }}
        onChange={handleImportFileChange}
      />

      {/* ── Sekcja: Plan kont ── */}
      <div className={styles.sectionBlock}>
        <div className={styles.sectionHeader} style={{ cursor: "pointer" }} onClick={() => setSec2Collapsed((v) => !v)}>
          {sec2Collapsed ? <ChevronRightIcon sx={{ fontSize: 14 }} /> : <ExpandMoreIcon sx={{ fontSize: 14 }} />}
          Plan kont
          <span style={{ marginLeft: "auto", display: "flex", gap: 8 }} onClick={(e) => e.stopPropagation()}>
            <button
              className={styles.planCreateBtn}
              onClick={() => { setZoisPlanTitle(""); setZoisPlanDialog(true); }}
            >
              <ContentCopyIcon sx={{ fontSize: 13 }} />
              Utwórz z ZOiS
            </button>
            <button
              className={styles.planCreateBtn}
              onClick={() => importFileRef.current?.click()}
            >
              <FileUploadIcon sx={{ fontSize: 13 }} />
              Importuj z pliku
            </button>
          </span>
        </div>

        {!sec2Collapsed && userPlans.length === 0 && (
          <div className={styles.sectionEmpty}>
            Brak planów kont. Utwórz plan importując plik lub klikając „Utwórz z ZOiS".
          </div>
        )}

        {!sec2Collapsed && userPlans.map((plan) => {
          const isActive = activePlanId === plan.id;
          const isPosting = postingPlanId === plan.id;
          const accountCount = countNodes(plan.tree);
          return (
            <div key={plan.id}>
              <div
                className={`${styles.planRow} ${isActive ? styles.planRowActive : ""}`}
                onClick={() => handleTogglePlan(plan.id)}
              >
                <span className={styles.planChevron}>
                  {isActive ? <ExpandMoreIcon sx={{ fontSize: 16 }} /> : <ChevronRightIcon sx={{ fontSize: 16 }} />}
                </span>
                <span className={styles.planTitle}>{plan.title}</span>
                <span style={{ fontSize: 11, color: "rgba(0,0,0,0.38)", flexShrink: 0, marginLeft: 8 }}>
                  {accountCount} {accountCount === 1 ? "konto" : accountCount >= 2 && accountCount <= 4 ? "konta" : "kont"}
                </span>
                {isPosting && <span className={styles.planBadgeActive}>aktywny</span>}
                <span className={styles.planActions} onClick={(e) => e.stopPropagation()}>
                  <Tooltip title={isPosting ? "Odznacz jako plan do dekretacji" : "Ustaw jako plan do dekretacji"} arrow>
                    <IconButton
                      size="small"
                      sx={{
                        color: isPosting ? "#f59e0b" : "rgba(0,0,0,0.25)",
                        "&:hover": { color: "#f59e0b" },
                      }}
                      onClick={() => handleSetPostingPlan(plan.id)}
                    >
                      {isPosting
                        ? <svg viewBox="0 0 24 24" width={16} height={16} fill="currentColor"><path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
                        : <svg viewBox="0 0 24 24" width={16} height={16} fill="currentColor"><path d="M22 9.24l-7.19-.62L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21 12 17.27 18.18 21l-1.63-7.03L22 9.24zM12 15.4l-3.76 2.27 1-4.28-3.32-2.88 4.38-.38L12 6.1l1.71 4.04 4.38.38-3.32 2.88 1 4.28L12 15.4z"/></svg>
                      }
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Usuń plan" arrow>
                    <IconButton
                      size="small"
                      sx={{ color: "rgba(0,0,0,0.38)", "&:hover": { color: "#d32f2f" } }}
                      onClick={() => setDeletePlanConfirm({ id: plan.id, title: plan.title })}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </span>
              </div>

              {isActive && (
                <PlanTree
                  tree={plan.tree}
                  onTreeChange={(tree) => handleUserPlanTreeChange(plan.id, tree)}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Pasek postępu importu */}
      <Dialog open={!!importProgress} maxWidth="xs" fullWidth disableEscapeKeyDown>
        <DialogContent sx={{ py: 3, px: 3 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: "rgba(0,0,0,0.75)", marginBottom: 10, textAlign: "center" }}>
            {importProgress?.label}
          </div>
          <LinearProgress
            variant="determinate"
            value={importProgress?.value ?? 0}
            sx={{
              height: 6,
              borderRadius: 3,
              "& .MuiLinearProgress-bar": { borderRadius: 3 },
            }}
          />
          <div style={{ fontSize: 11, color: "rgba(0,0,0,0.38)", textAlign: "right", marginTop: 6 }}>
            {importProgress?.value ?? 0}%
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog: import z pliku */}
      <Dialog
        open={!!importData}
        onClose={() => setImportData(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 15, fontWeight: 600, pb: 1 }}>
          Importuj plan kont z pliku
        </DialogTitle>
        {importData && (
          <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Nazwa planu *"
              size="small"
              fullWidth
              autoFocus
              value={importData.planTitle}
              onChange={(e) => setImportData((prev) => prev && ({ ...prev, planTitle: e.target.value }))}
              onKeyDown={(e) => e.key === "Enter" && handleImportConfirm()}
              placeholder="np. Plan kont 2025"
            />

            <Divider sx={{ my: 0.5 }} />

            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: "0.5px", textTransform: "uppercase", color: "#5d49c7", marginBottom: 4 }}>
              Opcje importu
            </div>

            <FormControlLabel
              sx={{ alignItems: "flex-start", mx: 0 }}
              control={
                <Checkbox
                  size="small"
                  checked={importData.setAsActive}
                  onChange={(e) => setImportData((prev) => prev && ({ ...prev, setAsActive: e.target.checked }))}
                  sx={{ pt: 0.3 }}
                />
              }
              label={
                <span style={{ fontSize: 13 }}>
                  Oznacz jako <strong>aktywny plan kont</strong> używany przy dekretacji księgowej
                </span>
              }
            />

            <FormControlLabel
              sx={{ alignItems: "flex-start", mx: 0 }}
              control={
                <Checkbox
                  size="small"
                  checked={importData.importBo}
                  onChange={(e) => setImportData((prev) => prev && ({ ...prev, importBo: e.target.checked }))}
                  sx={{ pt: 0.3 }}
                />
              }
              label={
                <span style={{ fontSize: 13 }}>
                  Wczytaj dane jako <strong>bilans otwarcia</strong> w Zestawieniu obrotów i sald
                </span>
              }
            />

            {importData.importBo && (
              <div style={{ display: "flex", flexDirection: "column", gap: 12, paddingLeft: 28 }}>
                <TextField
                  label="Data bilansu otwarcia"
                  type="date"
                  size="small"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={importData.boDate}
                  onChange={(e) => setImportData((prev) => prev && ({ ...prev, boDate: e.target.value }))}
                  helperText="Data, na którą bilans otwarcia został sporządzony (rok wyznacza rok księgowy)"
                />
                <div style={{ display: "flex", gap: 12 }}>
                <FormControl size="small" sx={{ flex: 1 }}>
                  <FormLabel sx={{ fontSize: 12, mb: 0.5 }}>Kolumna BO Wn</FormLabel>
                  <Select
                    value={importData.boWnCol}
                    onChange={(e) => setImportData((prev) => prev && ({ ...prev, boWnCol: Number(e.target.value) }))}
                    sx={{ fontSize: 13 }}
                  >
                    {importData.headers.map((h, i) => (
                      <MenuItem key={i} value={i} sx={{ fontSize: 13 }}>{h || `Kolumna ${i + 1}`}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ flex: 1 }}>
                  <FormLabel sx={{ fontSize: 12, mb: 0.5 }}>Kolumna BO Ma</FormLabel>
                  <Select
                    value={importData.boMaCol}
                    onChange={(e) => setImportData((prev) => prev && ({ ...prev, boMaCol: Number(e.target.value) }))}
                    sx={{ fontSize: 13 }}
                  >
                    {importData.headers.map((h, i) => (
                      <MenuItem key={i} value={i} sx={{ fontSize: 13 }}>{h || `Kolumna ${i + 1}`}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                </div>
              </div>
            )}
          </DialogContent>
        )}
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setImportData(null)} color="inherit" size="small">Anuluj</Button>
          <Button
            onClick={handleImportConfirm}
            variant="contained"
            size="small"
            disabled={!importData?.planTitle.trim()}
          >
            Importuj
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: utwórz plan z ZOiS */}
      <Dialog
        open={zoisPlanDialog}
        onClose={() => setZoisPlanDialog(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 15, fontWeight: 600, pb: 1 }}>
          Utwórz plan kont z ZOiS
        </DialogTitle>
        <DialogContent>
          <div className={styles.dialogHint} style={{ marginBottom: 12 }}>
            Plan kont zostanie zbudowany na podstawie symboli i nazw kont
            wczytanych w Zestawieniu obrotów i sald.
          </div>
          <TextField
            label="Nazwa planu *"
            size="small"
            fullWidth
            autoFocus
            value={zoisPlanTitle}
            onChange={(e) => setZoisPlanTitle(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleCreateFromZois()}
            placeholder="np. Plan kont 2025"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setZoisPlanDialog(false)} color="inherit" size="small">
            Anuluj
          </Button>
          <Button
            onClick={handleCreateFromZois}
            variant="contained"
            size="small"
            disabled={!zoisPlanTitle.trim()}
          >
            Utwórz
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog: usuń plan */}
      <Dialog
        open={!!deletePlanConfirm}
        onClose={() => setDeletePlanConfirm(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: 15, fontWeight: 600, pb: 1 }}>Usuń plan kont</DialogTitle>
        <DialogContent>
          Czy na pewno usunąć plan <strong>„{deletePlanConfirm?.title}"</strong>?
          <br />
          <span style={{ fontSize: 12, color: "rgba(0,0,0,0.5)", marginTop: 4, display: "block" }}>
            Tej operacji nie można cofnąć.
          </span>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeletePlanConfirm(null)} color="inherit" size="small">Anuluj</Button>
          <Button onClick={confirmDeletePlan} variant="contained" color="error" size="small">Usuń</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default WpkGminyTab;
